.root {
  background: var(--color-background);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  overflow: hidden;
  transition: border-color 0.15s, box-shadow 0.15s;

  &:focus-within {
    border-color: var(--color-focus);
    box-shadow: 0 0 0 2px var(--color-focus);
  }
}

.root-is-disabled {
  background: var(--color-secondary);
  opacity: 0.6;
}

.root-is-readOnly {
  border-style: dashed;
}

.toolbar {
  align-items: center;
  background: var(--color-surface);
  border-bottom: 1px solid var(--color-border);
  display: flex;
  flex-wrap: wrap;
  gap: 2px;
  padding: 4px 8px;
}

.toolbarBtn {
  align-items: center;
  background: transparent;
  border: 0;
  border-radius: 6px;
  color: var(--color-foreground);
  cursor: pointer;
  display: inline-flex;
  height: 28px;
  justify-content: center;
  padding: 0 8px;
  transition: background-color 0.1s;

  &:hover:not(:disabled) {
    background: var(--color-background);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }
}

.toolbarBtnActive {
  background: var(--color-background);
  border: 1px solid var(--color-border);
}

.toolbarDivider {
  background: var(--color-border);
  display: inline-block;
  height: 16px;
  margin: 0 4px;
  width: 1px;
}

// Extended-mode controls (heading level / table actions).
.toolbar-select {
  background: transparent;
  border: 0;
  border-radius: 6px;
  color: var(--color-foreground);
  cursor: pointer;
  font-size: 12px;
  height: 28px;
  padding: 0 4px;

  &:hover:not(:disabled) {
    background: var(--color-background);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }
}

// Color swatch: the icon + a native color input, sized like a toolbar button.
.toolbar-color {
  align-items: center;
  border-radius: 6px;
  color: var(--color-foreground);
  cursor: pointer;
  display: inline-flex;
  gap: 4px;
  height: 28px;
  padding: 0 6px;

  &:hover {
    background: var(--color-background);
  }

  input {
    background: transparent;
    border: 0;
    cursor: pointer;
    height: 16px;
    padding: 0;
    width: 20px;
  }
}

.body {
  color: var(--color-foreground);
  font-size: 14px;
  min-height: 120px;
  padding: 12px;
  position: relative;

  // Tiptap's ProseMirror root gets focus styles via the wrapper, so
  // strip its outline / padding here.
  :global(.ProseMirror) {
    min-height: 96px;
    outline: none;
  }

  :global(.ProseMirror p) {
    line-height: 1.5;
    margin: 0 0 8px;
  }

  :global(.ProseMirror p:last-child) {
    margin-bottom: 0;
  }

  :global(.ProseMirror ul),
  :global(.ProseMirror ol) {
    margin: 0 0 8px;
    padding-left: 20px;
  }

  :global(.ProseMirror a) {
    color: var(--color-focus);
    text-decoration: underline;
  }

  // Extended-mode structures (tables / inline images from legacy content).
  :global(.ProseMirror table) {
    border-collapse: collapse;
    margin: 0 0 8px;
    table-layout: fixed;
    width: 100%;
  }

  :global(.ProseMirror td),
  :global(.ProseMirror th) {
    border: 1px solid var(--color-border);
    box-sizing: border-box;
    min-width: 32px;
    padding: 4px 8px;
    position: relative;
    vertical-align: top;
  }

  :global(.ProseMirror th) {
    background: var(--color-surface);
    font-weight: 500;
    text-align: left;
  }

  :global(.ProseMirror .selectedCell)::after {
    background: rgb(37 99 235 / 8%);
    content: '';
    inset: 0;
    pointer-events: none;
    position: absolute;
  }

  :global(.ProseMirror img) {
    height: auto;
    max-width: 100%;
  }
}

.placeholder {
  color: var(--color-placeholder);
  font-size: 14px;
  left: 12px;
  pointer-events: none;
  position: absolute;
  top: 12px;
}

.char-count {
  padding: 4px 12px 0;
  text-align: right;
  font-size: 12px;
  color: var(--color-muted-fg);
}

.link {
  color: var(--color-focus);
  text-decoration: underline;
}
